import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Handshake } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";

export default function AffiliatePanel() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/affiliate/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return null;

  const copy = () => {
    navigator.clipboard.writeText(stats.url);
    toast.success("Your referral link is copied — share it anywhere.");
  };

  const tiles = [
    [stats.clicks, "Link clicks"],
    [stats.signups, "Signed up"],
    [stats.paying, "Paying now"],
    [`$${(stats.earned_cents / 100).toFixed(2)}`, "Earned"],
  ];

  return (
    <section className="mb-16 border border-white/10" data-testid="affiliate-section">
      <div className="p-8 border-b border-white/10 flex flex-wrap items-start justify-between gap-6">
        <div className="max-w-xl">
          <p className="overline-label mb-3 flex items-center gap-2"><Handshake size={14} className="text-accent" /> Affiliate program</p>
          <p className="font-display text-2xl mb-2">Earn <span className="italic text-accent">{stats.rate}% forever</span> on every member you bring in.</p>
          <p className="text-sm text-white/60">
            Share your link — every model or brand who joins and pays through it earns you {stats.rate}% of every payment they make,
            for as long as they stay a paying member. Payouts are settled by the office.{" "}
            <Link to="/affiliate-policy" data-testid="affiliate-policy-link" className="text-accent hover:underline">Read the affiliate policy</Link>
          </p>
        </div>
        <div className="flex items-center gap-3 min-w-0">
          <code className="font-grotesk text-xs text-white/70 border border-white/15 px-4 py-3 truncate max-w-[300px]" data-testid="affiliate-link">{stats.url}</code>
          <button data-testid="affiliate-copy" onClick={copy} className="btn-outline !px-4 !py-3 flex items-center gap-2 shrink-0"><Copy size={13} /> Copy</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10" data-testid="affiliate-stats">
        {tiles.map(([v, l]) => (
          <div key={l} className="bg-background p-6">
            <p className="font-display text-3xl mb-1">{v}</p>
            <p className="overline-label !text-[10px]">{l}</p>
          </div>
        ))}
      </div>
      {stats.referrals.length > 0 && (
        <div className="p-8 border-t border-white/10">
          <p className="overline-label !text-[10px] mb-6">Your referrals</p>
          <div className="flex flex-col divide-y divide-white/10">
            {stats.referrals.map((r) => (
              <div key={r.id} className="py-4 flex flex-wrap items-center gap-x-6 gap-y-1" data-testid={`referral-row-${r.id}`}>
                <p className="text-sm text-white/85 min-w-[160px]">{r.name}</p>
                <p className="text-xs text-white/40 flex-1">{r.email}</p>
                <p className="font-grotesk text-[10px] uppercase tracking-[0.12em] text-white/50">
                  {r.role === "model" ? "Talent" : "Brand"} · {r.signed_up ? new Date(r.signed_up).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                </p>
                <span className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 ${r.status === "paying" ? "border-accent text-accent" : "border-white/20 text-white/50"}`} data-testid={`referral-status-${r.id}`}>
                  {r.status}
                </span>
                <p className="font-grotesk text-xs text-white/70 w-20 text-right">${(r.earned_cents / 100).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {stats.recent_clicks.length > 0 && (
        <div className="p-8 border-t border-white/10">
          <p className="overline-label !text-[10px] mb-4">Recent clicks</p>
          <div className="flex flex-col gap-2">
            {stats.recent_clicks.map((c, i) => (
              <p key={i} className="font-grotesk text-[11px] text-white/45" data-testid={`affiliate-click-${i}`}>
                {new Date(c.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                {c.ref ? ` · via ${c.ref.replace(/^https?:\/\//, "").slice(0, 50)}` : " · direct"}
              </p>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
